import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, Download, Filter, Loader2, RefreshCw } from "lucide-react";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileTopBar } from "@/components/MobileTopBar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface DrillThroughPageShellProps {
  title: string;
  subtitle?: string;
  backLabel: string;
  sidebarOpen: boolean;
  onSidebarOpenChange: (open: boolean) => void;
  children: ReactNode;
  filterSlot?: ReactNode;
  exportOptions?: Array<{ label: string; value: string }>;
  selectedExportOptions?: string[];
  onToggleExportOption?: (value: string) => void;
  isExporting?: boolean;
  onExport?: () => void;
  error?: string | null;
  onRetry?: () => void;
}

export function DrillThroughPageShell({
  title,
  subtitle,
  backLabel,
  sidebarOpen,
  onSidebarOpenChange,
  children,
  filterSlot,
  exportOptions,
  selectedExportOptions = [],
  onToggleExportOption,
  isExporting = false,
  onExport,
  error,
  onRetry,
}: DrillThroughPageShellProps) {
  const navigate = useNavigate();

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={onSidebarOpenChange}>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />
        <div className="flex flex-1 flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title={title} />
          <main className="flex-1 overflow-auto p-2 sm:p-3 space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm">
              <div>
                <h1 className="text-lg font-semibold text-foreground">{title}</h1>
                {subtitle && (
                  <p className="text-xs text-muted-foreground">{subtitle}</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {filterSlot}

                {exportOptions && exportOptions.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 border-border/80 bg-background/70 px-3 shadow-sm hover:bg-accent"
                      >
                        <Filter className="h-4 w-4" />
                        Export Filter
                        <ChevronDown className="h-4 w-4 opacity-70" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-72 p-2 shadow-lg">
                      <p className="px-2 pb-2 pt-1 text-sm text-muted-foreground">
                        Choose export option
                      </p>
                      {exportOptions.map((opt) => (
                        <DropdownMenuCheckboxItem
                          key={opt.value}
                          className="rounded-md py-2.5 text-base font-medium"
                          checked={selectedExportOptions.includes(opt.value)}
                          onSelect={(event) => event.preventDefault()}
                          onCheckedChange={() =>
                            onToggleExportOption?.(opt.value)
                          }
                        >
                          {opt.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {onExport && (
                  <Button
                    size="sm"
                    className="gap-2 shadow-sm"
                    onClick={onExport}
                    disabled={isExporting || selectedExportOptions.length === 0}
                  >
                    {isExporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    {isExporting ? "Exporting..." : "Export Excel"}
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {backLabel}
                </Button>
              </div>
            </div>

            {error ? (
              <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border bg-card text-muted-foreground">
                <p className="text-sm">{error}</p>
                {onRetry && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={onRetry}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                  </Button>
                )}
              </div>
            ) : (
              children
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
